import { makeAutoObservable, runInAction } from 'mobx';
import { agent } from '../api/agent';
import {
  FeedItem, FeedParams, UrgencyParams, NewsItem, InfluenceProfile,
  FeedComment, CreatePostDto, CreateCommentDto,
  DEFAULT_FEED_PARAMS, DEFAULT_URGENCY_PARAMS,
  FeedCommentTargetType,
  FeedItemType,
  PostVisibility,
} from '../models/Playground';
import { Pagination, PagingParams } from '../models/Pagination';
import { toast } from 'react-toastify';
import i18n from "../../i18n";

export default class PlaygroundStore {
  // ── Active tab ─────────────────────────────────────────────────────────────
  activeTab: 'feed' | 'urgency' | 'news' = 'feed';

  // ── Feed ───────────────────────────────────────────────────────────────────
  feedItems: FeedItem[] = [];
  feedParams: FeedParams = DEFAULT_FEED_PARAMS;
  feedPagination: Pagination | null = null;
  loadingFeed = false;
  hasMoreFeed = true;

  // ── Urgency ────────────────────────────────────────────────────────────────
  urgencyItems: FeedItem[] = [];
  urgencyParams: UrgencyParams = DEFAULT_URGENCY_PARAMS;
  urgencyPagination: Pagination | null = null;
  loadingUrgency = false;
  hasMoreUrgency = true;

  // ── News ───────────────────────────────────────────────────────────────────
  newsItems: NewsItem[] = [];
  newsSportFilter: string = 'all';
  loadingNews = false;

  // ── Influence ──────────────────────────────────────────────────────────────
  influenceProfile: InfluenceProfile | null = null;
  loadingInfluence = false;

  // ── Create post ────────────────────────────────────────────────────────────
  createPostModalOpen = false;

  constructor() {
    // Load persisted filter settings — must happen in constructor,
    // after property declarations, to avoid TS2729 "used before initialization"
    this.feedParams = loadPersistedFeedParams();
    this.urgencyParams = loadPersistedUrgencyParams();
    this.newsSportFilter = loadPersistedNewsSport();

    makeAutoObservable(this);
  }

  // ── Tab ────────────────────────────────────────────────────────────────────
  setActiveTab = (tab: 'feed' | 'urgency' | 'news') => {
    this.activeTab = tab;
  };

  // ── Feed ───────────────────────────────────────────────────────────────────
  loadFeed = async (reset = false) => {
    if (reset) {
      runInAction(() => {
        this.feedItems = [];
        this.feedParams = { ...this.feedParams, pageNumber: 1 };
        this.hasMoreFeed = true;
      });
    }

    if (!this.hasMoreFeed && !reset) return;
    runInAction(() => { this.loadingFeed = true; });

    try {
      const result = await agent.Playground.getFeed(this.feedParams);
      runInAction(() => {
        const items = (result.data as unknown as FeedItem[]).map(this.hydrateDates);
        if (reset) {
          this.feedItems = items;
        } else {
          this.feedItems = [...this.feedItems, ...items];
        }
        this.feedPagination = result.pagination;
        this.hasMoreFeed = this.feedItems.length < result.pagination.totalItems;
        this.feedParams = { ...this.feedParams, pageNumber: this.feedParams.pageNumber + 1 };
        this.loadingFeed = false;
      });
    } catch {
      runInAction(() => { this.loadingFeed = false; });
    }
  };

  loadMoreFeed = () => {
    if (!this.loadingFeed && this.hasMoreFeed) this.loadFeed();
  };

  setFeedParams = (params: Partial<FeedParams>) => {
    this.feedParams = { ...this.feedParams, ...params };
    this.persistFeedParams();
    this.loadFeed(true);
  };

  // ── NEW: Deep-link helpers ───────────────────────────────────────────────

  /**
   * Ask the backend which page a given post lives on (using the current
   * feedParams.pageSize), then keep calling loadFeed(false) until that many
   * pages are loaded. Used by PlaygroundPage when opening a
   * ?highlightPost=<id> link from a notification.
   *
   * Returns the resolved pageNumber, or null if the post couldn't be located.
   */
  loadFeedToPostAsync = async (postId: string): Promise<number | null> => {
    try {
      const { pageNumber } = await agent.Playground.getFeedPostPage(
        postId,
        this.feedParams.pageSize
      );

      // feedParams.pageNumber is already the NEXT page to be fetched
      // (it gets incremented after each successful loadFeed call), so
      // "pages already loaded" = feedParams.pageNumber - 1.
      let pagesLoaded = this.feedParams.pageNumber - 1;

      while (pagesLoaded < pageNumber && this.hasMoreFeed) {
        await this.loadFeed(false);
        pagesLoaded += 1;
      }

      return pageNumber;
    } catch (err) {
      console.warn('loadFeedToPostAsync failed:', err);
      return null;
    }
  };

  // ── Post actions ───────────────────────────────────────────────────────────
  createPost = async (dto: CreatePostDto) => {
    try {
      const item = await agent.Playground.createPost(dto);
      runInAction(() => {
        this.feedItems = [this.hydrateDates(item), ...this.feedItems];
        this.createPostModalOpen = false;
      });
      toast.success((i18n as any).t('playground.success.postCreated'));
    } catch {
      toast.error((i18n as any).t('playground.errors.postCreateFailed'));
    }
  };

  deletePost = async (id: string) => {
    try {
      await agent.Playground.deletePost(id);
      runInAction(() => {
        this.feedItems = this.feedItems.filter(i => i.id !== id);
      });
      toast.success((i18n as any).t('playground.success.postDeleted'));
    } catch {
      toast.error((i18n as any).t('playground.errors.postDeleteFailed'));
    }
  };

  togglePostLike = async (id: string) => {
    try {
      await agent.Playground.likePost(id);
      runInAction(() => {
        const item = this.feedItems.find(i => i.id === id);
        if (item) {
          item.isLikedByCurrentUser = !item.isLikedByCurrentUser;
          item.likeCount += item.isLikedByCurrentUser ? 1 : -1;
        }
      });
    } catch { /* silent */ }
  };

  markNotRelevant = async (id: string) => {
    try {
      await agent.Playground.notRelevant(id);
      runInAction(() => {
        this.feedItems = this.feedItems.filter(i => i.id !== id);
      });
    } catch { /* silent */ }
  };

  sharePost = async (id: string) => {
    try {
      await agent.Playground.sharePost(id);
      runInAction(() => {
        const item = this.feedItems.find(i => i.id === id);
        if (item) item.shareCount += 1;
      });
      toast.success((i18n as any).t('playground.success.postShared'));
    } catch (e: any) {
      if (e?.response?.data === 'Already shared') {
        toast.warning((i18n as any).t('playground.errors.alreadyShared'));
      } else if (e?.response?.data === 'Cannot share your own post') {
        toast.warning((i18n as any).t('playground.errors.cannotShareOwn'));
      } else {
        toast.error((i18n as any).t('playground.errors.postShareFailed'));
      }
    }
  };

  // ── Comments ───────────────────────────────────────────────────────────────
  addComment = async (dto: CreateCommentDto): Promise<FeedComment | undefined> => {
    try {
      const comment = await agent.Playground.addComment(dto);
      const hydratedComment = this.hydrateCommentDates(comment);
      runInAction(() => {
        if (dto.feedPostId) {
          const item = this.feedItems.find(i => i.id === dto.feedPostId);
          if (item) {
            if (dto.parentCommentId) {
              const parent = item.topComments.find(c => c.id === dto.parentCommentId);
              if (parent) parent.replies = [...(parent.replies || []), hydratedComment];
            } else {
              item.topComments = [...item.topComments, hydratedComment];
              item.commentCount += 1;
            }
          }
        }
        if (dto.newsItemId) {
          const newsItem = this.newsItems.find(n => n.newsId === dto.newsItemId);
          if (newsItem) newsItem.commentCount += 1;
        }
      });
      return hydratedComment;
    } catch {
      toast.error((i18n as any).t('playground.errors.commentAddFailed'));
      return undefined;
    }
  };

  deleteComment = async (commentId: string) => {
    try {
      await agent.Playground.deleteComment(commentId);
      runInAction(() => {
        // Remove from feed post comments if present
        for (const item of this.feedItems) {
          const before = item.topComments.length;
          item.topComments = item.topComments.filter(c => c.id !== commentId);
          if (item.topComments.length < before) {
            item.commentCount = Math.max(0, item.commentCount - 1);
            break;
          }
        }
        // News item comment count is decremented locally in NewsCard via setComments
      });
    } catch {
      toast.error((i18n as any).t('playground.errors.commentDeleteFailed'));
    }
  };

  loadNewsComments = async (newsId: string): Promise<FeedComment[] | undefined> => {
    try {
      const comments = await agent.Playground.getNewsComments(newsId);
      return comments.map(this.hydrateCommentDates);
    } catch {
      toast.error((i18n as any).t('playground.errors.commentsLoadFailed'));
      return undefined;
    }
  };

  toggleCommentLike = async (commentId: string, postId: string) => {
    try {
      await agent.Playground.likeComment(commentId);
      runInAction(() => {
        const item = this.feedItems.find(i => i.id === postId);
        if (item) {
          const comment = this.findComment(item.topComments, commentId);
          if (comment) {
            comment.isLikedByCurrentUser = !comment.isLikedByCurrentUser;
            comment.likeCount += comment.isLikedByCurrentUser ? 1 : -1;
          }
        }
      });
    } catch { /* silent */ }
  };

  private findComment = (comments: FeedComment[], id: string): FeedComment | null => {
    for (const c of comments) {
      if (c.id === id) return c;
      const found = this.findComment(c.replies, id);
      if (found) return found;
    }
    return null;
  };

  // ── Urgency ────────────────────────────────────────────────────────────────
  loadUrgency = async (reset = false) => {
    if (reset) {
      runInAction(() => {
        this.urgencyItems = [];
        this.urgencyParams = { ...this.urgencyParams, pageNumber: 1 };
        this.hasMoreUrgency = true;
      });
    }

    if (!this.hasMoreUrgency && !reset) return;
    runInAction(() => { this.loadingUrgency = true; });

    try {
      const result = await agent.Playground.getUrgency(this.urgencyParams);
      runInAction(() => {
        const items = (result.data as unknown as FeedItem[]).map(this.hydrateDates);
        if (reset) {
          this.urgencyItems = items;
        } else {
          this.urgencyItems = [...this.urgencyItems, ...items];
        }
        this.urgencyPagination = result.pagination;
        this.hasMoreUrgency = this.urgencyItems.length < result.pagination.totalItems;
        this.urgencyParams = { ...this.urgencyParams, pageNumber: this.urgencyParams.pageNumber + 1 };
        this.loadingUrgency = false;
      });
    } catch {
      runInAction(() => { this.loadingUrgency = false; });
    }
  };

  loadMoreUrgency = () => {
    if (!this.loadingUrgency && this.hasMoreUrgency) this.loadUrgency();
  };

  setUrgencyParams = (params: Partial<UrgencyParams>) => {
    this.urgencyParams = { ...this.urgencyParams, ...params };
    this.persistUrgencyParams();
    this.loadUrgency(true);
  };

  // ── News ───────────────────────────────────────────────────────────────────
  loadNews = async () => {
    runInAction(() => { this.loadingNews = true; });
    try {
      const items = await agent.Playground.getNews(
        this.newsSportFilter !== 'all' ? this.newsSportFilter : undefined
      );
      runInAction(() => {
        this.newsItems = items.map(n => ({ ...n, publishedAt: new Date(n.publishedAt) }));
        this.loadingNews = false;
      });
    } catch {
      runInAction(() => { this.loadingNews = false; });
    }
  };

  setNewsSportFilter = (sport: string) => {
    this.newsSportFilter = sport;
    this.persistNewsSport();
    this.loadNews();
  };

  // ── Influence ──────────────────────────────────────────────────────────────
  loadInfluenceProfile = async (userId: string, historyDays = 30) => {
    runInAction(() => { this.loadingInfluence = true; });
    try {
      const profile = await agent.Playground.getInfluenceProfile(userId, historyDays);
      runInAction(() => {
        this.influenceProfile = {
          ...profile,
          history: profile.history.map(h => ({ ...h, date: new Date(h.date) }))
        };
        this.loadingInfluence = false;
      });
    } catch {
      runInAction(() => { this.loadingInfluence = false; });
    }
  };

  // ── Modal ──────────────────────────────────────────────────────────────────
  setCreatePostModalOpen = (open: boolean) => {
    this.createPostModalOpen = open;
  };

  // ── Date hydration ─────────────────────────────────────────────────────────
  private hydrateDates = (item: FeedItem): FeedItem => {
    const typeMap: Record<string, FeedItemType> = {
      'NeedOnePlayer': FeedItemType.NeedOnePlayer,
      'PublicGame':    FeedItemType.PublicGame,
      'FriendActivity':FeedItemType.FriendActivity,
      'UserPost':      FeedItemType.UserPost,
      'SharedPost':    FeedItemType.SharedPost,
      'NewsItem':      FeedItemType.NewsItem,
      'Tournament':    FeedItemType.Tournament,
    };
  
    const visibilityMap: Record<string, PostVisibility> = {
      'FriendsOnly': PostVisibility.FriendsOnly,
      'Followers':   PostVisibility.Followers,
      'Standard':    PostVisibility.Standard,
    };
  
    const resolvedType = typeof item.type === 'string'
      ? (typeMap[item.type as string] ?? Number(item.type))
      : item.type;
  
    const resolvedVisibility = item.visibility != null && typeof item.visibility === 'string'
      ? (visibilityMap[item.visibility as string] ?? item.visibility)
      : item.visibility;
  
    return {
      ...item,
      type: resolvedType as FeedItemType,
      visibility: resolvedVisibility as PostVisibility,
      createdAt: new Date(item.createdAt),
      game: item.game ? { ...item.game, dateTimeStart: new Date(item.game.dateTimeStart) } : undefined,
      tournament: item.tournament
        ? {
            ...item.tournament,
            startsAt: new Date(item.tournament.startsAt),
            registrationClosesAt: new Date(item.tournament.registrationClosesAt),
          }
        : undefined,
      topComments: (item.topComments || []).map(this.hydrateCommentDates),
    };
  };

  private hydrateCommentDates = (c: FeedComment): FeedComment => ({
    ...c,
    createdAt: new Date(c.createdAt),
    replies: (c.replies || []).map(this.hydrateCommentDates),
  });

  // ── Persistence helpers (delegates to module-level functions) ────────────
  persistFeedParams = () => {
    try { localStorage.setItem('pg_feedParams', JSON.stringify(this.feedParams)); } catch { }
  };

  persistUrgencyParams = () => {
    try { localStorage.setItem('pg_urgencyParams', JSON.stringify(this.urgencyParams)); } catch { }
  };

  persistNewsSport = () => {
    try { localStorage.setItem('pg_newsSport', this.newsSportFilter); } catch { }
  };
}

// =============================================================================
// Module-level persistence helpers
// Defined outside the class so they can be called safely from the constructor
// before makeAutoObservable runs.
// =============================================================================

function loadPersistedFeedParams(): FeedParams {
  try {
    const raw = localStorage.getItem('pg_feedParams');
    return raw
      ? { ...DEFAULT_FEED_PARAMS, ...JSON.parse(raw), pageNumber: 1 }
      : DEFAULT_FEED_PARAMS;
  } catch {
    return DEFAULT_FEED_PARAMS;
  }
}

function loadPersistedUrgencyParams(): UrgencyParams {
  try {
    const raw = localStorage.getItem('pg_urgencyParams');
    return raw
      ? { ...DEFAULT_URGENCY_PARAMS, ...JSON.parse(raw), pageNumber: 1 }
      : DEFAULT_URGENCY_PARAMS;
  } catch {
    return DEFAULT_URGENCY_PARAMS;
  }
}

function loadPersistedNewsSport(): string {
  try {
    return localStorage.getItem('pg_newsSport') || 'all';
  } catch {
    return 'all';
  }
}